import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
    Smartphone, 
    Building2,
    Trash2,
    Plus,
    CheckCircle2,
    Clock,
    AlertTriangle,
    Phone,
    Hash,
    RefreshCw,
} from 'lucide-react';
import { apiClient, API_ENDPOINTS, useAuthStore } from '@dukene/shared';
import ConnectProviderModal from '../../components/settings/ConnectProviderModal';

export default function PaymentSettings() {
    const queryClient = useQueryClient();
    const activeStoreId = useAuthStore((state) => state.activeStoreId);
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

    const { isLoading: isStoreLoading } = useQuery({
        queryKey: ['merchant-store', activeStoreId],
        queryFn: () => apiClient.get(API_ENDPOINTS.MERCHANT.STORE),
        enabled: !!activeStoreId,
    });

    const { data: channelsResponse, isLoading: isChannelsLoading } = useQuery<any>({
        queryKey: ['merchant-channels', activeStoreId],
        queryFn: () => apiClient.get(API_ENDPOINTS.MERCHANT.CHANNELS.LIST),
        enabled: !!activeStoreId,
    });
    const channels = channelsResponse?.channels || (Array.isArray(channelsResponse) ? channelsResponse : []);

    const verifyChannelMutation = useMutation({
        mutationFn: (id: string) =>
            apiClient.post(API_ENDPOINTS.MERCHANT.CHANNELS.VERIFY(id), {}),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['merchant-channels', activeStoreId] }),
    });

    const activateChannelMutation = useMutation({
        mutationFn: (id: string) =>
            apiClient.post(API_ENDPOINTS.MERCHANT.CHANNELS.ACTIVATE(id), {}),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['merchant-channels', activeStoreId] }),
    });

    const deleteChannelMutation = useMutation({
        mutationFn: (id: string) =>
            apiClient.delete(API_ENDPOINTS.MERCHANT.CHANNELS.DELETE(id)),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['merchant-channels', activeStoreId] });
            setConfirmDelete(null);
        },
    });

    if (isStoreLoading || isChannelsLoading) return (
        <div className="flex h-64 items-center justify-center">
            <div className="w-8 h-8 border-2 border-emerald border-t-transparent rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="space-y-6 pb-20 max-w-4xl">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-[#D2D5D9] dark:border-slate-800">
                <div>
                    <h1 className="text-xl font-bold text-[#202223] dark:text-white">Payments</h1>
                </div>

                <button
                    onClick={() => setIsModalOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-[#202223] dark:bg-white text-white dark:text-slate-900 font-medium text-xs rounded-lg shadow-sm hover:opacity-90 transition-all self-start sm:self-auto"
                >
                    <Plus className="w-4 h-4 stroke-[2.5]" />
                    <span>Connect Channel</span>
                </button>
            </div>

            {/* Channels List */}
            <div className="space-y-4">
                <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                        <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Connected Providers ({channels.length})</h2>
                    </div>
                </div>

                {isChannelsLoading || isStoreLoading ? (
                    <div className="p-12 text-center rounded-2xl border border-dashed border-gray-200 bg-gray-50/50">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-emerald border-r-transparent mb-3" />
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Loading payment providers...</p>
                    </div>
                ) : channels.length === 0 ? (
                    <div className="p-12 text-center rounded-2xl border border-dashed border-gray-200 bg-gray-50/50 space-y-4">
                        <div className="w-12 h-12 rounded-2xl bg-gray-100 text-gray-400 flex items-center justify-center mx-auto font-bold">
                            <Smartphone className="w-6 h-6" />
                        </div>
                        <div className="space-y-1 max-w-sm mx-auto">
                            <h3 className="font-bold text-gray-900 text-base">No payment channels connected</h3>
                            <p className="text-xs text-gray-500 leading-relaxed">
                                Connect your first M-Pesa Till, Paybill, or Bank Paybill to start accepting payments on your storefront.
                            </p>
                        </div>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="h-10 px-4 rounded-xl bg-gray-900 hover:bg-gray-800 text-white font-bold text-xs transition-all inline-flex items-center gap-2"
                        >
                            <Plus className="w-3.5 h-3.5" /> Connect Channel
                        </button>
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {channels.map((channel: any) => (
                            <div key={channel.id} className="flex flex-col p-6 rounded-2xl border border-gray-100 shadow-sm bg-white gap-4 transition-all hover:border-gray-200">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-emerald/10 text-emerald shrink-0 font-bold">
                                            {channel.channelType === 'BANK' ? <Building2 className="w-6 h-6" /> : <Smartphone className="w-6 h-6" />}
                                        </div>
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-base font-bold text-gray-900">{channel.businessName}</span>
                                                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-gray-100 text-gray-700">
                                                    {channel.channelType === 'TILL' ? 'Buy Goods (Till)' : channel.channelType === 'BANK' ? (channel.bankName || 'Bank Paybill') : 'Paybill'}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-4 text-xs text-gray-500 font-medium flex-wrap">
                                                <span className="flex items-center gap-1">
                                                    <Hash className="w-3.5 h-3.5 text-gray-400" />
                                                    <strong>{channel.channelType === 'TILL' ? 'Till Number:' : channel.channelType === 'BANK' ? 'Bank Paybill:' : 'Paybill Number:'}</strong> {channel.channelNumber}
                                                </span>
                                                {(channel.channelType === 'PAYBILL' || channel.channelType === 'BANK') && channel.accountNumber && (
                                                    <span className="flex items-center gap-1">
                                                        <Hash className="w-3.5 h-3.5 text-gray-400" />
                                                        <strong>Account:</strong> {channel.accountNumber}
                                                    </span>
                                                )}
                                                <span className="flex items-center gap-1">
                                                    <Phone className="w-3.5 h-3.5 text-gray-400" />
                                                    <strong>Phone:</strong> {channel.ownerPhone}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 self-start sm:self-end md:self-center w-full sm:w-auto mt-2 md:mt-0">
                                        {/* Active Checkout Badge */}
                                        {channel.isActive && (
                                            <span className="px-3 py-1.5 rounded-xl text-xs font-bold bg-emerald text-white flex items-center gap-1.5 shadow-sm">
                                                <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                                                <span>ACTIVE FOR CHECKOUT</span>
                                            </span>
                                        )}

                                        {/* Status Badge when not active for checkout */}
                                        {!channel.isActive && channel.status === 'ACTIVE' && (
                                            <span className="px-3 py-1.5 rounded-xl text-xs font-bold bg-emerald/10 text-emerald flex items-center gap-1.5 border border-emerald/20">
                                                <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                                                <span>VERIFIED</span>
                                            </span>
                                        )}
                                        {!channel.isActive && channel.status === 'PENDING' && (
                                            <span className="px-3 py-1.5 rounded-xl text-xs font-bold bg-amber-50 text-amber-700 flex items-center gap-1.5 border border-amber-200">
                                                <Clock className="w-3.5 h-3.5 shrink-0 animate-pulse" />
                                                <span>PENDING VERIFICATION</span>
                                            </span>
                                        )}
                                        {!channel.isActive && channel.status === 'MANUAL_LOA_REQUIRED' && (
                                            <span className="px-3 py-1.5 rounded-xl text-xs font-bold bg-orange-50 text-orange-700 flex items-center gap-1.5 border border-orange-200">
                                                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                                <span>LOA REQUIRED</span>
                                            </span>
                                        )}
                                        {!channel.isActive && channel.status === 'FAILED_VERIFICATION' && (
                                            <span className="px-3 py-1.5 rounded-xl text-xs font-bold bg-red-50 text-red-700 flex items-center gap-1.5 border border-red-200">
                                                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                                <span>FAILED VERIFICATION</span>
                                            </span>
                                        )}
                                        {!channel.isActive && channel.status === 'REJECTED' && (
                                            <span className="px-3 py-1.5 rounded-xl text-xs font-bold bg-gray-100 text-gray-700 flex items-center gap-1.5 border border-gray-200">
                                                <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-gray-500" />
                                                <span>REJECTED</span>
                                            </span>
                                        )}
                                        {!channel.isActive && channel.status === 'INACTIVE' && (
                                            <span className="px-3 py-1.5 rounded-xl text-xs font-bold bg-gray-100 text-gray-600 flex items-center gap-1.5 border border-gray-200">
                                                <Clock className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                                                <span>INACTIVE</span>
                                            </span>
                                        )}

                                        {/* Set as Active Button */}
                                        {!channel.isActive && channel.status === 'ACTIVE' && (
                                            <button
                                                onClick={() => activateChannelMutation.mutate(channel.id)}
                                                disabled={activateChannelMutation.isPending}
                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald text-white rounded-xl text-xs font-bold hover:bg-emerald-600 transition-colors disabled:opacity-50 shadow-sm"
                                                title="Set this channel as active for checkout"
                                            >
                                                <CheckCircle2 className={`w-3.5 h-3.5 ${activateChannelMutation.isPending ? 'animate-spin' : ''}`} />
                                                <span>Set as Active</span>
                                            </button>
                                        )}

                                        {/* Check Status Button for non-active PSP verification status */}
                                        {channel.status !== 'ACTIVE' && (
                                            <button
                                                onClick={() => verifyChannelMutation.mutate(channel.id)}
                                                disabled={verifyChannelMutation.isPending}
                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-xl text-xs font-bold hover:bg-gray-200 transition-colors disabled:opacity-50"
                                                title="Refresh verification status"
                                            >
                                                <RefreshCw className={`w-3.5 h-3.5 ${verifyChannelMutation.isPending ? 'animate-spin' : ''}`} />
                                                <span>Check Status</span>
                                            </button>
                                        )}

                                        {/* Delete Button */}
                                        {confirmDelete === channel.id ? (
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => deleteChannelMutation.mutate(channel.id)}
                                                    className="px-3 py-1.5 bg-red-500 text-white rounded-xl text-xs font-bold hover:bg-red-600 transition-colors"
                                                >
                                                    Confirm
                                                </button>
                                                <button
                                                    onClick={() => setConfirmDelete(null)}
                                                    className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-xl text-xs font-bold hover:bg-gray-200 transition-colors"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => setConfirmDelete(channel.id)}
                                                className="flex items-center justify-center w-8 h-8 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                title="Remove Channel"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* LOA Guidance Notice */}
                                {channel.status === 'MANUAL_LOA_REQUIRED' && (
                                    <div className="mt-2 p-4 rounded-xl bg-orange-50/80 border border-orange-200/60 flex flex-col gap-2">
                                        <div className="flex items-center gap-2 text-xs font-bold text-orange-900">
                                            <AlertTriangle className="w-4 h-4 text-orange-600 shrink-0" />
                                            <span>Safaricom Letter of Authorization (LOA) Required</span>
                                        </div>
                                        <p className="text-xs text-orange-800 leading-relaxed">
                                            To securely link your M-Pesa channel to our Payment aggregator without requiring your login passwords, Safaricom requires a signed Letter of Authorization. Please submit your signed LOA form to <strong>support@dukene.co.ke</strong> or contact our WhatsApp support team to complete activation.
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal */}
            <ConnectProviderModal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
            />
        </div>
    );
}
